import React, { useState, useEffect } from "react";
import TopNavigation from "components/ui/TopNavigation";
import Icon from "components/AppIcon";
import VendorKPICard from "./components/VendorKPICard";
import VendorTable from "./components/VendorTable";
import PlatformAlerts from "./components/PlatformAlerts";
import BillingOverview from "./components/BillingOverview";
import VendorManagement from "./components/VendorManagement";
import SubscriptionPlans from "./components/SubscriptionPlans";
import VendorPlanAssignment from "./components/VendorPlanAssignment";
import VendorBilling from "./components/VendorBilling";
import TrialVendors from "./components/TrialVendors";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export default function SuperAdminDashboard() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState({
    totalVendors: 0,
    activeVendors: 0,
    inactiveVendors: 0,
    totalMembers: 0,
    totalRevenue: 0,
    totalPayments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleDeleteAllData = async () => {
    if (deleteConfirmText !== 'DELETE ALL DATA') return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.post('/super-admin/reset-platform-data');
      setDeleteSuccess(true);
      setDeleteConfirmText('');
      fetchStats();
    } catch (err) {
      console.error('Error resetting platform data:', err);
      setDeleteError(err?.message || 'Failed to delete data. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      navigate('/login-page');
    } catch (err) {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: analytics } = await api.get('/super-admin/analytics');

      const totalVendors = parseInt(analytics?.vendors?.total_vendors || 0);
      const activeVendors = parseInt(analytics?.vendors?.active_vendors || 0);
      const inactiveVendors = parseInt(analytics?.vendors?.suspended_vendors || 0);
      const totalMembers = parseInt(analytics?.members?.total_members || 0);
      const totalRevenue = parseFloat(analytics?.revenue?.total_collected || 0);

      setStats({
        totalVendors,
        activeVendors,
        inactiveVendors,
        totalMembers,
        totalRevenue,
        totalPayments: 0,
      });
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const kpiData = [
    {
      title: "Active Vendors",
      value: loading ? "..." : String(stats?.activeVendors),
      change: loading ? "" : `${stats?.inactiveVendors} inactive`,
      changeType: "neutral",
      icon: "Building2",
      iconColor: "var(--color-primary)",
    },
    {
      title: "Platform Revenue",
      value: loading ? "..." : stats?.totalRevenue?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
      change: "+MRR",
      changeType: "positive",
      icon: "DollarSign",
      iconColor: "var(--color-accent)",
      prefix: "$",
    },
    {
      title: "Total Members",
      value: loading ? "..." : String(stats?.totalMembers),
      change: "platform-wide",
      changeType: "positive",
      icon: "Users",
      iconColor: "var(--color-secondary)",
    },
    {
      title: "Total Vendors",
      value: loading ? "..." : String(stats?.totalVendors),
      change: `${stats?.activeVendors} active`,
      changeType: "neutral",
      icon: "RefreshCw",
      iconColor: "var(--color-warning)",
    },
    {
      title: "Total Payments",
      value: loading ? "..." : String(stats?.totalPayments),
      change: "all time",
      changeType: "positive",
      icon: "CreditCard",
      iconColor: "var(--color-error)",
    },
    {
      title: "System Health",
      value: "99.8",
      change: "+0.1%",
      changeType: "positive",
      icon: "Activity",
      iconColor: "var(--color-success)",
      suffix: "%",
    },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="pt-[10px]">
        {/* Page Header */}
        <div className="bg-[var(--color-card)] border-b border-[var(--color-border)] px-4 md:px-6 py-4">
          <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-md bg-[var(--color-accent)]/15 flex items-center justify-center">
                  <Icon name="ShieldCheck" size={15} color="var(--color-accent)" />
                </div>
                <h1 className="text-lg font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Super Admin Dashboard</h1>
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--color-success)]/10 text-[var(--color-success)] text-xs font-medium font-[var(--font-caption)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-success)] animate-pulse"></span>
                  Live
                </span>
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Multi-tenant platform management · {loading ? '...' : stats?.activeVendors} active vendors
              </p>
            </div>
            <div className="flex items-center gap-2">
              {error && (
                <span className="text-xs text-[var(--color-error)] font-[var(--font-caption)] px-2 py-1 bg-[var(--color-error)]/10 rounded-md">
                  {error}
                </span>
              )}
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-border)] rounded-md bg-[var(--color-card)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors">
                <Icon name="Download" size={13} color="currentColor" />
                Export Report
              </button>
              <button
                onClick={() => { setActiveTab("vendors"); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
              >
                <Icon name="Plus" size={13} color="white" />
                New Vendor
              </button>
              <button
                onClick={() => navigate('/account-settings')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-border)] text-[var(--color-muted-foreground)] rounded-md hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
              >
                <Icon name="Settings" size={13} color="currentColor" />
                Settings
              </button>
              <button
                onClick={() => setShowLogoutModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-error)]/30 text-[var(--color-error)] rounded-md hover:bg-[var(--color-error)]/5 hover:border-[var(--color-error)]/50 transition-colors"
              >
                <Icon name="LogOut" size={13} color="var(--color-error)" />
                Logout
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6 space-y-6">
          {/* KPI Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {kpiData?.map((kpi, i) => (
              <VendorKPICard key={i} {...kpi} />
            ))}
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-1 border-b border-[var(--color-border)]">
            {[
              { id: "overview", label: "Overview", icon: "LayoutDashboard" },
              { id: "vendors", label: "Vendor Roster", icon: "Building2" },
              { id: "plans", label: "Subscription Plans", icon: "Package" },
              { id: "assignments", label: "Plan Assignments", icon: "UserCheck" },
              { id: "billing", label: "Vendor Billing", icon: "Receipt" },
            ]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium font-[var(--font-caption)] border-b-2 transition-colors -mb-px ${
                  activeTab === tab?.id
                    ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                    : "border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                }`}
              >
                <Icon name={tab?.icon} size={13} color="currentColor" />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Main: Vendor Table */}
                <div className="xl:col-span-2 space-y-6">
                  <VendorTable />
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                  <TrialVendors />
                </div>
              </div>

              {/* Danger Zone */}
              {/* <div className="border border-[var(--color-error)]/30 rounded-[var(--radius-md)] bg-[var(--color-error)]/5 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="AlertTriangle" size={15} color="var(--color-error)" />
                  <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-error)]">Danger Zone</h3>
                </div>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-3">
                  Permanently delete all platform data — vendors, members, branches, payments, subscriptions, and more. Your super admin account will be preserved.
                </p>
                <button
                  onClick={() => { setDeleteError(null); setDeleteSuccess(false); setDeleteConfirmText(''); setShowDeleteModal(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-error)]/40 text-[var(--color-error)] rounded-md hover:bg-[var(--color-error)]/10 hover:border-[var(--color-error)]/60 transition-colors"
                >
                  <Icon name="Trash2" size={13} color="var(--color-error)" />
                  Delete All Data
                </button>
              </div> */}
            </div>
          )}

          {activeTab === "vendors" && (
            <VendorManagement />
          )}

          {activeTab === "plans" && (
            <SubscriptionPlans />
          )}

          {activeTab === "assignments" && (
            <VendorPlanAssignment />
          )}

          {activeTab === "billing" && (
            <VendorBilling />
          )}
        </div>
      </main>
      {/* Delete All Data Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !deleting && setShowDeleteModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-error)]/40 rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] p-6 w-full max-w-md">
            {deleteSuccess ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-12 h-12 rounded-full bg-[var(--color-success)]/10 flex items-center justify-center">
                  <Icon name="CheckCircle2" size={22} color="var(--color-success)" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Data Deleted</h2>
                  <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                    All platform data has been permanently deleted. Your super admin account has been preserved.
                  </p>
                </div>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="w-full px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Delete All Platform Data</h2>
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">This action is permanent and cannot be undone.</p>
                  </div>
                </div>

                <div className="bg-[var(--color-error)]/8 border border-[var(--color-error)]/20 rounded-md p-3 space-y-1">
                  <p className="text-xs font-semibold text-[var(--color-error)] font-[var(--font-caption)]">The following will be permanently deleted:</p>
                  <ul className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] space-y-0.5 list-disc list-inside">
                    <li>All vendors, staff, trainers, and members</li>
                    <li>All branches, packages, and gym settings</li>
                    <li>All payments, invoices, and subscriptions</li>
                    <li>All workout plans, diet plans, and progress data</li>
                    <li>All classes, attendance records, and commissions</li>
                  </ul>
                  <p className="text-xs text-[var(--color-success)] font-semibold font-[var(--font-caption)] mt-2">Your super admin account will be preserved.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1.5">
                    Type <span className="font-bold font-[var(--font-data)] text-[var(--color-error)]">DELETE ALL DATA</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    disabled={deleting}
                    placeholder="DELETE ALL DATA"
                    className="w-full px-3 py-2 text-sm font-[var(--font-data)] border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:border-[var(--color-error)] disabled:opacity-50"
                  />
                </div>

                {deleteError && (
                  <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)] bg-[var(--color-error)]/10 px-3 py-2 rounded-md">{deleteError}</p>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all font-[var(--font-caption)] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAllData}
                    disabled={deleting || deleteConfirmText !== 'DELETE ALL DATA'}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {deleting ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Deleting...</>
                    ) : (
                      <><Icon name="Trash2" size={14} color="#fff" />Delete All Data</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !loggingOut && setShowLogoutModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] p-6 w-full max-w-sm">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="LogOut" size={22} color="var(--color-error)" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Log Out?</h2>
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                  Are you sure you want to log out?
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  disabled={loggingOut}
                  className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all font-[var(--font-caption)] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {loggingOut ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Logging out...</>
                  ) : (
                    <><Icon name="LogOut" size={14} color="#fff" />Log Out</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
